import React from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  GiftIcon,
  LinkIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import PlatformClientStateBreakdown from './PlatformClientStateBreakdown';

function StatCard({ icon: Icon, label, value, sublabel, gradient, onClick }) {
  const content = (
    <div className={`rounded-2xl border border-white/70 bg-white/80 p-5 shadow-soft backdrop-blur-sm   ${onClick ? 'cursor-pointer transition-transform hover:scale-[1.01]' : ''}`}>
      <div className={`mb-3 inline-flex rounded-xl bg-gradient-to-br ${gradient} p-2.5 text-white shadow-soft`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 ">{label}</p>
      <p className="mt-1 text-3xl font-bold tabular-nums text-secondary-900 ">{value}</p>
      {sublabel ? <p className="mt-1 text-xs text-secondary-500 ">{sublabel}</p> : null}
    </div>
  );

  if (onClick) {
    return (
      <motion.button type="button" onClick={onClick} whileTap={{ scale: 0.98 }} className="w-full text-left">
        {content}
      </motion.button>
    );
  }

  return content;
}

function QuickLink({ icon: Icon, label, value, onClick, colorClass }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl border border-secondary-200/80 bg-white p-4 text-left transition hover:border-orange-200 hover:bg-orange-50/30   "
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colorClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-secondary-500 ">{label}</p>
        <p className="text-xl font-bold text-secondary-900 ">{value}</p>
      </div>
    </button>
  );
}

function CompanyRow({ company, onSelect, t, rank }) {
  const stats = company.stats || {};
  return (
    <button
      type="button"
      onClick={() => onSelect?.(company.id)}
      className="flex w-full flex-wrap items-center gap-4 rounded-2xl border border-secondary-200/80 bg-white p-4 text-left transition-colors hover:border-orange-200 hover:bg-orange-50/30    "
    >
      {rank ? (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-sm font-bold text-secondary-700  ">
          {rank}
        </div>
      ) : null}
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-sm font-bold text-white">
        {company.name?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-secondary-900 ">{company.name}</p>
        <p className="text-xs text-secondary-500 ">{company.slug}</p>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-secondary-600 ">
        <span>{stats.vendors || 0} {t.platformAdmin.vendorsShort}</span>
        <span>{stats.clients || 0} {t.platformAdmin.clientsShort}</span>
        <span>{stats.referrals || 0} {t.platformAdmin.referralsShort}</span>
        <span>${(stats.revenue || 0).toLocaleString()}</span>
      </div>
    </button>
  );
}

const USER_TYPE_META = {
  admin: { icon: ShieldCheckIcon, className: 'bg-amber-100 text-amber-700  ' },
  vendor: { icon: UserGroupIcon, className: 'bg-red-100 text-red-700  ' },
  client: { icon: UsersIcon, className: 'bg-emerald-100 text-emerald-700  ' },
};

function ActivityRow({ entry, pa }) {
  const typeLabel = entry.user_type === 'admin'
    ? pa.adminUserType
    : entry.user_type === 'vendor'
      ? pa.vendorUserType
      : pa.clientUserType;
  const meta = USER_TYPE_META[entry.user_type] || USER_TYPE_META.client;
  const Icon = meta.icon;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-secondary-200/70 p-3 ">
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.className}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-secondary-900 ">{entry.username}</p>
        <p className="text-xs text-secondary-500 ">
          {typeLabel}
          {entry.company_name ? ` · ${entry.company_name}` : ''}
        </p>
      </div>
      <p className="shrink-0 text-xs text-secondary-500 ">
        {new Date(entry.created_at).toLocaleDateString()}
      </p>
    </div>
  );
}

function PlatformHomeView({
  overview,
  loading,
  loadError = false,
  onRetry,
  username,
  onOpenCompanies,
  onOpenUsers,
  onOpenAnalytics,
  onOpenSettings,
  onOpenPendingGifts,
  onOpenProspects,
  onSelectCompany,
}) {
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const totals = overview?.totals || {};
  const companies = overview?.companies || [];
  const recentUsers = overview?.recent_users || [];
  const topCompanies = overview?.top_companies || companies.slice(0, 5);
  const clientStates = overview?.client_states || {};
  const pendingGifts = totals.pending_gifts || 0;

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-36 animate-pulse rounded-2xl bg-secondary-100 " />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-dashed border-secondary-300 p-10 text-center ">
        <p className="text-secondary-600 ">{pa.overviewLoadError}</p>
        <button
          type="button"
          onClick={() => {
            void onRetry?.();
          }}
          className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          {pa.retry}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      {pendingGifts > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-3 rounded-2xl border border-pink-200/80 bg-pink-50/80 p-4 sm:flex-row sm:items-center sm:justify-between  "
        >
          <div className="flex items-start gap-3">
            <GiftIcon className="mt-0.5 h-5 w-5 shrink-0 text-pink-600 " />
            <p className="text-sm font-medium text-pink-900 ">
              {pa.alertPendingGifts.replace('{count}', String(pendingGifts))}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenPendingGifts?.()}
            className="shrink-0 rounded-lg bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-pink-700"
          >
            {pa.viewPendingGifts}
          </button>
        </motion.div>
      ) : null}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-orange-200/50 bg-gradient-to-br from-orange-500 to-amber-500 p-6 text-white shadow-glow md:p-8"
      >
        <p className="text-sm font-medium text-white/80">{pa.superAdminBadge}</p>
        <h2 className="mt-1 text-2xl font-bold md:text-3xl">{pa.welcome.replace('{name}', username || pa.adminFallback)}</h2>
        <p className="mt-2 max-w-2xl text-sm text-white/85">{pa.homeSubtitle}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={onOpenCompanies} className="rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/25">{pa.viewCompanies}</button>
          <button type="button" onClick={onOpenUsers} className="rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/25">{pa.viewAllUsers}</button>
          <button type="button" onClick={onOpenAnalytics} className="rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/25">{pa.viewAnalytics}</button>
          <button type="button" onClick={onOpenSettings} className="rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/25">{pa.openSettings}</button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={BuildingOffice2Icon} label={pa.totalCompanies} value={totals.companies || 0} sublabel={pa.activeTenants} gradient="from-violet-500 to-indigo-500" onClick={onOpenCompanies} />
        <StatCard icon={UsersIcon} label={pa.totalUsers} value={(totals.admins || 0) + (totals.vendors || 0) + (totals.clients || 0)} sublabel={`${totals.clients || 0} ${pa.clientsShort}, ${totals.vendors || 0} ${pa.vendorsShort}`} gradient="from-orange-500 to-amber-500" onClick={onOpenUsers} />
        <StatCard icon={LinkIcon} label={pa.totalReferrals} value={totals.referrals || 0} sublabel={pa.acrossAllCompanies} gradient="from-red-500 to-pink-500" onClick={onOpenAnalytics} />
        <StatCard icon={CurrencyDollarIcon} label={pa.platformRevenue} value={`$${(totals.revenue || 0).toLocaleString()}`} sublabel={pa.estimatedRevenue} gradient="from-emerald-500 to-teal-500" onClick={onOpenAnalytics} />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3 xl:gap-6">
        <div className="xl:col-span-2 space-y-5 xl:space-y-6">
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-secondary-900 ">{pa.topPerformingCompanies}</h3>
              <button type="button" onClick={onOpenCompanies} className="text-sm font-medium text-orange-600 hover:text-orange-700 ">{pa.viewAll}</button>
            </div>
            <div className="space-y-3">
              {topCompanies.length > 0 ? (
                topCompanies.map((company, index) => (
                  <CompanyRow key={company.id} company={company} rank={index + 1} onSelect={onSelectCompany} t={t} />
                ))
              ) : (
                <p className="rounded-2xl border border-dashed border-secondary-300 p-8 text-center text-secondary-500  ">{pa.noCompanies}</p>
              )}
            </div>
          </section>

          <section className="card-premium p-5">
            <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.clientStateBreakdown}</h3>
            <PlatformClientStateBreakdown clientStates={clientStates} />
          </section>
        </div>

        <div className="space-y-5 xl:space-y-6">
          <section className="space-y-3">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.systemPulse}</h3>
            <QuickLink
              icon={ClipboardDocumentListIcon}
              label={pa.totalProspects}
              value={totals.prospects || 0}
              onClick={onOpenProspects}
              colorClass="bg-cyan-100 text-cyan-600  "
            />
            <QuickLink
              icon={GiftIcon}
              label={pa.pendingGifts}
              value={totals.pending_gifts || 0}
              onClick={onOpenPendingGifts}
              colorClass="bg-pink-100 text-pink-600  "
            />
            <QuickLink
              icon={ShieldCheckIcon}
              label={pa.companyAdmins}
              value={totals.admins || 0}
              onClick={onOpenUsers}
              colorClass="bg-amber-100 text-amber-600  "
            />
            <QuickLink
              icon={ChartBarIcon}
              label={pa.sidebarSales || pa.viewAnalytics}
              value={totals.referrals || 0}
              onClick={onOpenAnalytics}
              colorClass="bg-red-100 text-red-600  "
            />
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-secondary-900 ">{pa.recentActivity}</h3>
              <button type="button" onClick={onOpenUsers} className="text-sm font-medium text-orange-600 ">{pa.viewAll}</button>
            </div>
            <div className="space-y-2">
              {recentUsers.length > 0 ? (
                recentUsers.slice(0, 6).map((entry) => (
                  <ActivityRow key={`${entry.user_type}-${entry.id}-${entry.created_at}`} entry={entry} pa={pa} />
                ))
              ) : (
                <p className="text-sm text-secondary-500 ">{pa.noRecentActivity}</p>
              )}
            </div>
          </section>

          <button type="button" onClick={onOpenSettings} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-orange-200/60 bg-orange-50/50 px-4 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-100/70   ">
            <Cog6ToothIcon className="h-4 w-4" />
            {pa.manageAllSettings}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlatformHomeView;
